// Organization > Regions: every region a brand has.

import { useMemo, useState } from "react";
import { Pencil } from "lucide-react";
import { useTenantOrganization } from "../../lib/api";
import { filterRegions, indexOrg, regionRows } from "../../lib/organization";
import { useRegionEditor } from "./useOrgEditors";
import { FieldError, FieldLabel, inputClass } from "./primitives";
import {
  Cell,
  ConfirmDelete,
  EmptyState,
  ErrorState,
  FormDialog,
  Loading,
  PageIntro,
  SearchBox,
  Table,
  Toolbar,
} from "./OrgKit";

export default function Regions({ tenantId, readOnly = false }) {
  const { data, isLoading, isError, error, refetch } = useTenantOrganization(tenantId);
  const [query, setQuery] = useState("");

  const ix = useMemo(() => indexOrg(data), [data]);
  const {
    open,
    setOpen,
    editing,
    name,
    setName,
    errors,
    openAdd,
    openEdit,
    submit,
    pending,
    deleting,
    setDeleting,
    askDelete,
    confirmDelete,
  } = useRegionEditor(tenantId, ix);
  const rows = useMemo(() => regionRows(ix), [ix]);
  const shown = useMemo(() => filterRegions(rows, { query }), [rows, query]);

  if (isLoading) return <Loading />;
  if (isError) return <ErrorState error={error} onRetry={refetch} />;

  return (
    <>
      <PageIntro
        title="Regions"
        actionLabel={readOnly ? null : "Add region"}
        onAction={openAdd}
      />

      {rows.length > 0 && (
        <Toolbar count={`${shown.length} of ${rows.length}`}>
          <SearchBox value={query} onChange={setQuery} placeholder="Search regions" data-testid="region-search" />
        </Toolbar>
      )}

      {rows.length === 0 ? (
        <EmptyState
          title="No regions yet"
          body="Add the first one."
          actionLabel={readOnly ? null : "Add region"}
          onAction={openAdd}
        />
      ) : shown.length === 0 ? (
        <EmptyState title="Nothing matches that search" body="Clear it, or add the region that should be here." />
      ) : (
        <Table columns={["Region", "Dealers", "Workshops", "People"]}>
          {shown.map((row) => (
            <tr key={row.id} data-testid="region-row">
              <Cell>
                <div className="text-[14px] font-medium text-[#111827]">{row.name}</div>
              </Cell>
              <Cell>{row.dealers}</Cell>
              <Cell>{row.workshops}</Cell>
              <Cell>{row.people}</Cell>
              <Cell className="whitespace-nowrap text-right">
                {!readOnly && (
                  <>
                <button
                  type="button"
                  onClick={() => openEdit(row)}
                  aria-label={`Rename ${row.name}`}
                  className="mr-1 inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#9CA3AF] hover:bg-[#F3F4F6] hover:text-[#111827]"
                >
                  <Pencil size={15} />
                </button>
                <button
                  type="button"
                  onClick={() => askDelete(row.id)}
                  aria-label={`Delete ${row.name}`}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#9CA3AF] hover:bg-[#FEF2F2] hover:text-[#DC2626]"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" />
                  </svg>
                </button>
                  </>
                )}
              </Cell>
            </tr>
          ))}
        </Table>
      )}

      <FormDialog
        open={open}
        onOpenChange={setOpen}
        title={editing ? `Rename ${editing.name}` : "Add region"}
        onSubmit={submit}
        submitLabel={editing ? "Save changes" : "Add region"}
        pending={pending}
      >
        <div>
          <FieldLabel>Region name</FieldLabel>
          <input
            type="text"
            className={inputClass}
            value={name}
            placeholder="West"
            autoFocus
            onChange={(e) => setName(e.target.value)}
          />
          <FieldError>{errors.name}</FieldError>
        </div>
      </FormDialog>

      <ConfirmDelete
        open={!!deleting}
        onOpenChange={(v) => !v && setDeleting(null)}
        name={deleting?.name || ""}
        body="This cannot be undone."
        blocked={deleting?.blocked}
        onConfirm={confirmDelete}
        pending={pending}
      />
    </>
  );
}
